/**
 * useAgentRun — drives a single agent run end-to-end.
 *
 * One `streamRun` call both starts the run and yields its SSE events, which
 * are folded into the assistant message + a tool-call timeline. Cancelling
 * is just aborting the fetch — the server treats the dropped connection as
 * a cancel. Designed to plug into the existing React Query cache shape used
 * by `useChatHook`, so the rest of the Chat UI keeps working unmodified.
 */

import { useCallback, useRef } from "react";

import { streamRun } from "../services/agentRunService";

/**
 * Mutates the assistant placeholder message in-place to mirror the existing
 * useChatHook contract. Returns the message reference so callers can
 * propagate it into the React Query cache via setQueryData.
 */
function ensureSteps(assistantMessage) {
  if (!assistantMessage.steps) assistantMessage.steps = [];
  return assistantMessage.steps;
}

function findOrAddStep(assistantMessage, stepIndex) {
  const steps = ensureSteps(assistantMessage);
  let step = steps.find((s) => s.index === stepIndex);
  if (!step) {
    step = { index: stepIndex, toolCalls: [] };
    steps.push(step);
  }
  return step;
}

function findToolCall(assistantMessage, id) {
  for (const step of ensureSteps(assistantMessage)) {
    const found = step.toolCalls.find((tc) => tc.id === id);
    if (found) return found;
  }
  return null;
}

export function useAgentRun({ onUpdate, onDone, onError } = {}) {
  const abortRef = useRef(null);

  const start = useCallback(
    async ({ clientId, conversationId, message, assistantMessage }) => {
      const controller = new AbortController();
      abortRef.current = controller;
      let runId = null;

      try {
        for await (const evt of streamRun(
          clientId,
          { conversationId, message },
          { signal: controller.signal },
        )) {
          if (evt.event === "ready" && evt.data?.run_id) {
            runId = evt.data.run_id;
          }
          handleEvent(evt, assistantMessage);
          onUpdate?.(assistantMessage, evt);

          if (evt.event === "final") {
            onDone?.(assistantMessage, evt);
            return { runId, ok: true };
          }
          if (evt.event === "error") {
            onError?.(evt.data, assistantMessage);
            return { runId, ok: false, error: evt.data };
          }
        }
        // Stream ended without a final/error — treat as error.
        onError?.({ kind: "stream_closed" }, assistantMessage);
        return { runId, ok: false, error: { kind: "stream_closed" } };
      } catch (err) {
        if (controller.signal.aborted) {
          // user-initiated cancel — already surfaced
          return { runId, ok: false, error: { kind: "cancelled" } };
        }
        onError?.({ kind: "network", message: String(err) }, assistantMessage);
        return { runId, ok: false, error: { kind: "network" } };
      } finally {
        // Tool calls are only meant to be visible while the run is streaming.
        // Clearing `steps` here covers every terminal path (final, error,
        // stream-closed, network throw, user cancel) so the timeline
        // disappears once the message is complete. ToolTimeline returns
        // null when steps is empty, so no renderer changes are needed.
        delete assistantMessage.steps;
        // Same reasoning for the "Thinking…" pill. The `final` and `error`
        // handlers clear the flag, but cancel / stream_closed / a network
        // throw return without passing through them — leaving the pill
        // spinning on a run that has already stopped.
        delete assistantMessage.thinking;
        abortRef.current = null;
      }
    },
    [onUpdate, onDone, onError],
  );

  const cancel = useCallback(() => {
    // Aborting the fetch drops the connection; the server flips the run's
    // cancel flag on disconnect. No cancel endpoint round trip needed.
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return { start, cancel };
}

/**
 * Apply one SSE envelope to the in-place assistant message.
 *
 * The mutation pattern matches useChatHook today: the same message object is
 * shared between the React Query cache and this loop, so updates are visible
 * once the caller calls setQueryData.
 */
export function handleEvent(evt, assistantMessage) {
  switch (evt.event) {
    case "ready":
    case "run.started":
      // Lifecycle markers — nothing to fold into the message.
      return;

    case "step.started":
      findOrAddStep(assistantMessage, evt.data.step ?? 0);
      return;

    case "token": {
      if (assistantMessage.thinking) delete assistantMessage.thinking;
      assistantMessage.content =
        (assistantMessage.content || "") + (evt.data.text || "");
      return;
    }

    case "thought": {
      if (!assistantMessage.thoughts) assistantMessage.thoughts = "";
      assistantMessage.thoughts += evt.data.text || "";
      return;
    }

    case "tool_call": {
      const step = findOrAddStep(assistantMessage, evt.data.step ?? 0);
      const existing = step.toolCalls.find((tc) => tc.id === evt.data.id);
      if (existing) {
        existing.name = evt.data.name || existing.name;
        existing.title = evt.data.title;
        existing.arguments = evt.data.arguments || existing.arguments;
        existing.status = "running";
      } else {
        step.toolCalls.push({
          id: evt.data.id,
          name: evt.data.name,
          title: evt.data.title,
          arguments: evt.data.arguments,
          status: "running",
        });
      }
      return;
    }

    case "tool_result": {
      const tc = findToolCall(assistantMessage, evt.data.id);
      if (tc) {
        tc.status = evt.data.ok ? "ok" : "error";
        tc.latencyMs = evt.data.latency_ms;
        tc.error = evt.data.error;
      }
      // Bring through any table metadata so MessageDisplay can render the
      // CSV table the same way it does for the legacy endpoint.
      if (Array.isArray(evt.data.table_metadata) && evt.data.table_metadata.length) {
        assistantMessage.tableMetadata = [
          ...(assistantMessage.tableMetadata || []),
          ...evt.data.table_metadata,
        ];
      }
      return;
    }

    case "final": {
      // The final text is also the result of accumulated `token` events; we
      // just clear the thinking flag in case there were no tokens.
      // Capture the server-issued message id so this assistant turn can be
      // addressed (e.g. for feedback) without waiting for a reload.
      if (evt.data?.message_id != null) {
        assistantMessage.id = evt.data.message_id;
      }
      delete assistantMessage.thinking;
      return;
    }

    case "error": {
      assistantMessage.error = evt.data;
      delete assistantMessage.thinking;
      return;
    }

    default:
      return;
  }
}
